/**
 * MIT License
 *
 * Copyright (c) 2023 Jamasp Team
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import { useEffect, useCallback, useState } from 'react';
import { useAlert } from '../context/AlertContext';
import { useRegistration } from '../context/RegistrationContext';
import { checkProjectForRegistration } from '../services/RegistrationService';
import EmailRegistrationDirectStepper from '../components/register/EmailRegistrationDirectStepper';

export default function EmailRegistrationDirect() {
  const {
    email,
    setEmail,
    projectId,
    activeStep,
    loading,
    storeEmail,
    participantId,
    initiateFitbitAuth,
    renewRegistration,
    handleError,
    verifyEmail,
    runCronJobOnDate
  } = useRegistration();

  const { addAlert } = useAlert();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    console.debug('EmailRegistration:useEffect:projectId', projectId);
    const checkProjectStatus = async () => {
      if (!projectId) return;
      try {
        const result = await checkProjectForRegistration(projectId);
        console.debug('EmailRegistration:checkProjectStatus', result);
        setIsOpen(result)
        if (!result) {
          console.error("Check project status failed", result);
        }
      } catch (err) {
        handleError(err, 'Error checking project status. Please try again.');
      }
    };
    checkProjectStatus();
  }, [projectId, addAlert, handleError]);

  const handleEmailChange = useCallback((newEmail) => {
    setEmail(newEmail);
  }, [setEmail]);

  const handleStoreEmail = useCallback(() => {
    storeEmail();
  }, [storeEmail]);

  const handleInitiateFitbitAuth = useCallback(() => {
    initiateFitbitAuth();
  }, [initiateFitbitAuth]);

  if (!isOpen) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <Typography variant="h5">This project is not open for registration.</Typography>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Paper elevation={3} sx={{ padding: 4 }}>
        <Typography variant="h4" align="center" marginBottom={"1em"}>
          Welcome to Email Registration!
        </Typography>

        <EmailRegistrationDirectStepper
          email={email}
          activeStep={activeStep}
          participantId={participantId}
          loading={loading}
          onEmailChange={handleEmailChange}
          onStoreEmail={handleStoreEmail}
          onInitiateFitbitAuth={handleInitiateFitbitAuth}
          onRenewRegistration={renewRegistration}
          onVerifyEmail={verifyEmail}
          runCronJobOnDate={runCronJobOnDate}
        />
      </Paper>
    </Container>
  );
}